import React, { useMemo, useState } from "react";
import { useLang } from "../i18n";
import DottedSurface from "./DottedSurface";
import Header from "./Header";
import Footer from "./Footer";

const projectMeta = [
  {
    index: "01",
    title: "UBU Platform",
    img: "/UBUDemo.png",
    href: "https://ubu.live",
    tags: ["NestJS", "React", "PostgreSQL", "AWS", "Docker"],
  },
  {
    index: "02",
    title: "Mind Miner",
    img: "/mind-miner.png",
    href: "https://codingcops.com/case-studies/mindminer/",
    tags: ["React", "Node.js", "MongoDB", "OpenAI"],
  },
  {
    index: "03",
    title: "CS Student Chatbot",
    img: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80",
    href: null,
    tags: ["OpenAI", "Python", "Embeddings"],
  },
];

function Projects() {
  const { t } = useLang();
  const [active, setActive] = useState("all");
  const projects = projectMeta.map((project, index) => ({
    ...project,
    ...t.projects.items[index],
  }));
  const tags = useMemo(() => {
    const unique = [];
    projectMeta.forEach((project) => {
      project.tags.forEach((tag) => {
        if (!unique.includes(tag)) unique.push(tag);
      });
    });
    return unique;
  }, []);
  const visible = active === "all"
    ? projects
    : projects.filter((project) => project.tags.includes(active));

  return (
    <div className="projects-page relative flex min-h-dvh flex-col">
      <DottedSurface />
      <Header wide />
      <main className="relative z-[1] mx-auto w-full max-w-[1040px] flex-1 px-5 pb-20 pt-10 sm:px-8">
        <div className="section-label">
          <h1>{t.projects.heading}</h1>
          <span>{String(visible.length).padStart(2, "0")}</span>
        </div>
        <div className="project-filters" role="toolbar" aria-label={t.projects.filter}>
          <FilterChip label={t.projects.all} pressed={active === "all"} onClick={() => setActive("all")} />
          {tags.map((tag) => (
            <FilterChip key={tag} label={tag} pressed={active === tag} onClick={() => setActive(tag)} />
          ))}
        </div>
        {visible.length === 0 ? (
          <p className="text-muted mt-10 font-mono text-[13px]">{t.projects.empty}</p>
        ) : (
          <div className="project-grid">
            {visible.map((project) => (
              <ProjectCard
                key={project.title}
                project={project}
                openLabel={t.projects.open}
                onTag={setActive}
              />
            ))}
          </div>
        )}
      </main>
      <Footer wide />
    </div>
  );
}

function FilterChip({ label, pressed, onClick }) {
  return (
    <button
      type="button"
      className={`skill-chip${pressed ? " is-on" : ""}`}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function ProjectCard({ project, openLabel, onTag }) {
  const copy = (
    <>
      <img src={project.img} alt={project.alt} className="w-full object-cover" />
      <div className="min-w-0 px-3 pt-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[14px] font-medium tracking-[-0.01em]">
            <span className="text-faint mr-2 font-mono text-[11px]">{project.index}</span>
            {project.title}
          </h2>
          {project.href ? <span className="open-hint">{openLabel}</span> : null}
        </div>
        <p className="text-muted mt-1.5 text-[12px] leading-5">{project.desc}</p>
      </div>
    </>
  );

  return (
    <article className="project-card">
      {project.href ? (
        <a className="project-card-link" href={project.href} target="_blank" rel="noopener noreferrer">
          {copy}
        </a>
      ) : (
        <div>{copy}</div>
      )}
      <div className="flex flex-wrap gap-1.5 px-3 pb-3 pt-2">
        {project.tags.map((tag) => (
          <button key={tag} type="button" className="skill-chip" onClick={() => onTag(tag)}>
            {tag}
          </button>
        ))}
      </div>
    </article>
  );
}

export default Projects;
